// w44 Oltin yo'l (The Golden Line railway journey) — train-ticket preview
// card.
//
// The full template opens on a ticket-punch splash and carries the couple
// through a railway journey to the wedding. The catalog card compresses
// that into one still frame: a warm golden-hour gradient backdrop, a
// cream ticket-shaped panel (dashed border, punched notches cut into its
// left/right edges like a real train ticket), a dashed-ring punch circle
// holding the couple's initials monogram, cursive-styled names, a date
// rule, tiny rust/teal accent dots, and a small train silhouette rule
// along the bottom edge. Uses the site's already-loaded serif stack (via
// `font`) for the monogram + names only — no new font import.
import { type LayoutCtx } from "./_shared";

// Split "Timur & Madina" / "Jasur va Gulnora" / "Дилшод и Мадина" into
// initials joined with "&", matching the splash monogram. A single name
// collapses to a single initial — no dangling separator.
function initials(names: string): string {
  const parts = names
    .split(/\s*[&+—]\s*|\s+(?:va|и)\s+/i)
    .map((p) => p.trim())
    .filter(Boolean);
  const letters = parts.map((p) => p.charAt(0).toUpperCase());
  return letters.length > 1 ? `${letters[0]}&${letters[1]}` : letters[0] || "";
}

export function OltinYolPreviewLayout({ names, date, accent, ink, font }: LayoutCtx) {
  const rust = accent || "#b3552f";
  const cream = "#fbf3e2";
  const dusk = ink || "#3f2d20";
  const teal = "#2f6e63";
  const mono = initials(names);

  return (
    <div
      style={{
        position: "absolute",
        inset: 0,
        // Warm golden-hour gradient, dusk pooling at the base.
        background: `linear-gradient(180deg, #e8b96a 0%, ${rust} 45%, ${dusk} 100%)`,
        padding: "8%",
        display: "flex",
        alignItems: "center",
        justifyContent: "center",
        overflow: "hidden",
      }}
    >
      {/* The ticket. */}
      <div
        style={{
          position: "relative",
          width: "100%",
          background: cream,
          border: `1px dashed ${rust}99`,
          borderRadius: 4,
          boxShadow: "0 10px 30px rgba(0,0,0,0.35)",
          padding: "9% 8%",
          display: "flex",
          flexDirection: "column",
          alignItems: "center",
          textAlign: "center",
        }}
      >
        {/* Punched notches — cut into the left/right edges like a real
            train-ticket stub, sharing the backdrop's mid-tone so they read
            as holes rather than dots. */}
        <div
          style={{
            position: "absolute",
            left: -7,
            top: "50%",
            transform: "translateY(-50%)",
            width: 14,
            height: 14,
            borderRadius: "50%",
            background: rust,
          }}
        />
        <div
          style={{
            position: "absolute",
            right: -7,
            top: "50%",
            transform: "translateY(-50%)",
            width: 14,
            height: 14,
            borderRadius: "50%",
            background: rust,
          }}
        />

        <div
          style={{
            fontFamily: "var(--font-ui)",
            fontSize: 6,
            fontWeight: 700,
            letterSpacing: "0.32em",
            color: rust,
            marginBottom: "9%",
          }}
        >
          OLTIN YO'L
        </div>

        {/* Dashed-ring punch circle carrying the initials monogram. */}
        <div
          style={{
            width: 46,
            height: 46,
            borderRadius: "50%",
            border: `1.5px dashed ${rust}`,
            display: "flex",
            alignItems: "center",
            justifyContent: "center",
            marginBottom: "9%",
          }}
        >
          <span
            style={{
              fontFamily: font,
              fontSize: 15,
              color: dusk,
            }}
          >
            {mono}
          </span>
        </div>

        {/* Names. */}
        <div
          style={{
            fontFamily: font,
            fontSize: 14,
            lineHeight: 1.25,
            color: dusk,
            marginBottom: "7%",
          }}
        >
          {names}
        </div>

        {/* Rust date rule flanked by tiny rust/teal accent dots. */}
        <div style={{ display: "flex", alignItems: "center", gap: 6, marginBottom: "9%" }}>
          <span style={{ width: 4, height: 4, borderRadius: "50%", background: teal }} />
          <div style={{ width: 34, height: 1, background: `${rust}bb` }} />
          <span style={{ width: 4, height: 4, borderRadius: "50%", background: rust }} />
        </div>

        <div
          style={{
            fontFamily: "var(--font-ui)",
            fontSize: 6.5,
            fontWeight: 700,
            letterSpacing: "0.26em",
            color: dusk,
            marginBottom: "10%",
          }}
        >
          {date.toUpperCase()}
        </div>

        {/* Small train silhouette rule along the ticket's foot. */}
        <div style={{ display: "flex", alignItems: "flex-end", gap: 2 }}>
          <div style={{ width: 16, height: 6, borderRadius: "2px 2px 0 0", background: rust }} />
          <div style={{ width: 3, height: 8, background: rust }} />
          <div style={{ width: 3, height: 3, borderRadius: "50%", background: dusk }} />
          <div style={{ width: 3, height: 3, borderRadius: "50%", background: dusk }} />
        </div>
      </div>
    </div>
  );
}
